import { useState, useEffect, useRef } from "react";
import { AppConfig } from "../AppConfig";
import { Outlet, useLocation } from "react-router-dom";
import classNames from "classnames";
import { Tooltip } from "primereact/tooltip";
import PrimeReact from "primereact/api";
import "primereact/resources/primereact.css";
import { CSSTransition } from "react-transition-group";
import dashboard from "../assets/assests/dashboard.svg";
import usericon from "../assets/assests/user.svg";
import branchicon from "../assets/assests/branch.svg";
import machineicon from "../assets/assests/machine.svg";
import notiicon from "../assets/assests/notification.svg";
import kioskicon from "../assets/assests/kiosk.svg";
import customericon from "../assets/assests/customer.svg";
import auditicon from "../assets/assests/audit.svg";
import securityicon from "../assets/assests/security.svg";
import serviceicon from "../assets/assests/service.svg";
import clockicon from "../assets/assests/clock.svg";
import { AppTopbar } from "../app/features/components/app_top_bar";
import { AppMenu } from "../app/features/components/app_menu";


const DashboardLayout = ({ isLoggedIn, handleLogout }) => {
  const copyTooltipRef = useRef();
  const location = useLocation();
  const [layoutMode, setLayoutMode] = useState("static");
  const [layoutColorMode, setLayoutColorMode] = useState("light");
  const [inputStyle, setInputStyle] = useState("outlined")  ;
  const [ripple, setRipple] = useState(true);
  const [staticMenuInactive, setStaticMenuInactive] = useState(false);
  const [overlayMenuActive, setOverlayMenuActive] = useState(false);
  const [mobileMenuActive, setMobileMenuActive] = useState(false);
  const [mobileTopbarMenuActive, setMobileTopbarMenuActive] = useState(false);
  const [currentRouteLabel, setCurrentRouteLabel] = useState("");
  PrimeReact.ripple = true;

  let menuClick = false;
  let mobileTopbarMenuClick = false;

  useEffect(() => {
    if (mobileMenuActive) {
      addClass(document.body, "body-overflow-hidden");
    } else {
      removeClass(document.body, "body-overflow-hidden");
    }
  }, [mobileMenuActive]);

  useEffect(() => {
    copyTooltipRef &&
      copyTooltipRef.current &&
      copyTooltipRef.current.updateTargetEvents();
  }, [location]);

  const onInputStyleChange = (inputStyle) => {
    setInputStyle(inputStyle);
  };

  const onRipple = (e) => {
    PrimeReact.ripple = e.value;
    setRipple(e.value);
  };

  const onLayoutModeChange = (mode) => {
    setLayoutMode(mode);
  };

  const onColorModeChange = (mode) => {
    setLayoutColorMode(mode);
  };

  const onWrapperClick = (event) => {
    if (!menuClick) {
      setOverlayMenuActive(false);
      setMobileMenuActive(false);
    }

    if (!mobileTopbarMenuClick) {
      setMobileTopbarMenuActive(false);
    }

    mobileTopbarMenuClick = false;
    menuClick = false;
  };

  const onToggleMenuClick = (event) => {
    menuClick = true;

    if (isDesktop()) {
      if (layoutMode === "overlay") {
        if (mobileMenuActive === true) {
          setOverlayMenuActive(true);
        }

        setOverlayMenuActive((prevState) => !prevState);
        setMobileMenuActive(false);
      } else if (layoutMode === "static") {
        setStaticMenuInactive((prevState) => !prevState);
      }
    } else {
      setMobileMenuActive((prevState) => !prevState);
    }

    event.preventDefault();
  };

  const onSidebarClick = () => {
    menuClick = true;
  };

  const onMobileTopbarMenuClick = (event) => {
    mobileTopbarMenuClick = true;

    setMobileTopbarMenuActive((prevState) => !prevState);
    event.preventDefault();
  };

  const onMobileSubTopbarMenuClick = (event) => {
    mobileTopbarMenuClick = true;

    event.preventDefault();
  };

  const isDesktop = () => {
    return window.innerWidth >= 992;
  };

  const addClass = (element, className) => {
    if (element.classList) element.classList.add(className);
    else element.className += " " + className;
  };

  const removeClass = (element, className) => {
    if (element.classList) element.classList.remove(className);
    else
      element.className = element.className.replace(
        new RegExp(
          "(^|\\b)" + className.split(" ").join("|") + "(\\b|$)",
          "gi"
        ),
        " "
      );
  };

  const wrapperClass = classNames("layout-wrapper", {
    "layout-overlay": layoutMode === "overlay",
    "layout-static": layoutMode === "static",
    "layout-static-sidebar-inactive":
      staticMenuInactive && layoutMode === "static",
    "layout-overlay-sidebar-active":
      overlayMenuActive && layoutMode === "overlay",
    "layout-mobile-sidebar-active": mobileMenuActive,
    "p-input-filled": inputStyle === "filled",
    "p-ripple-disabled": ripple === false,
    "layout-theme-light": layoutColorMode === "light",
  });

  const onMenuItemClick = (event) => {
    if (!event.item.items) {
      setOverlayMenuActive(false);
      setMobileMenuActive(false);
    }
  };

  const findCurrentRouteLabel = (location) => {
    const path = location.pathname;

    const findLabel = (menuItems, parentLabel = "") => {
      for (const item of menuItems) {
        if (item.to === path) {
          return parentLabel ? (
            <>
              {parentLabel}&nbsp; <i className="pi pi-arrow-right"></i> &nbsp;
              {item.label}
            </>
          ) : (
            item.label
          );
        }

        // Check for a parent match
        if (
          item.to !== path &&
          path.startsWith(item.to) &&
          item.to !== "/" &&
          !path.includes(":")
        ) {
          return parentLabel ? (
            <>
              {parentLabel}&nbsp; <i className="pi pi-arrow-right"></i>&nbsp;{" "}
              {item.label}
            </>
          ) : (
            item.label
          );
        }

        // If there are nested items, check recursively
        if (item.items) {
          const nestedLabel = findLabel(item.items, item.label);
          if (nestedLabel) {
            return parentLabel ? (
              <>
                {parentLabel}&nbsp; <i className="pi pi-arrow-right"></i>&nbsp;
                {nestedLabel}
              </>
            ) : (
              nestedLabel
            );
          }
        }
      }
      return null;
    };

    const matchedLabel = findLabel(menu);

    return matchedLabel || "Unknown";
  };

  useEffect(() => {
    setCurrentRouteLabel(findCurrentRouteLabel(location));
  }, [location]);

  const menu = [
    {
      label: "Dashboard",
      to: "/",
      icon: dashboard,
    },
    {
      label: "User Management",
      to: "/user-management",
      icon: usericon,
    },
    {
      label: "Branch Management",
      to: "/branch-management",
      icon: branchicon,
    },
    {
      label: "Kiosk Management",
      to: "/kiosk-management",
      icon: kioskicon,
    },
    {
      label: "Customer Management",
      to: "/main-customer-services-management",
      icon: customericon,
    },
    {
      label: "Machine Management",
      to: "/machine-management",
      icon: machineicon,
    },
    {
      label: "User Activity & Reports",
      to: "/user-activity-reports",
      icon: auditicon,
    },
    {
      label: "Security & Access Control",
      to: "/security-access-control",
      icon: securityicon,
    },
    {
      label: "Service Mode",
      to: "/service-mode",
      icon: serviceicon,
    },
    {
      label: "Queue Management",
      to: "/queue-management",
      icon: clockicon,
    },
    {
      label: "Alert & Notification",
      to: "/alert-notification",
      icon: notiicon,
    },
  ];

  return (
    <>
      <div className={wrapperClass} onClick={onWrapperClick}>
        <Tooltip
          ref={copyTooltipRef}
          target=".block-action-copy"
          position="bottom"
          content="Copied to clipboard"
          event="focus"
        />

        <div className="layout-sidebar" onClick={onSidebarClick}>
          <AppMenu
            model={menu}
            layoutColorMode={layoutColorMode}
            onMenuItemClick={onMenuItemClick}
          />
        </div>

        <div className="layout-main-container">
          <AppTopbar
            isLoggedIn={isLoggedIn}
            handleLogout={handleLogout}
            onToggleMenuClick={onToggleMenuClick}
            layoutColorMode={layoutColorMode}
            mobileTopbarMenuActive={mobileTopbarMenuActive}
            onMobileTopbarMenuClick={onMobileTopbarMenuClick}
            onMobileSubTopbarMenuClick={onMobileSubTopbarMenuClick}
            currentRouteLabel={currentRouteLabel}
          />
          <div className="layout-main">
            <Outlet />
          </div>
        </div>
      </div>
      <AppConfig
        rippleEffect={ripple}
        onRippleEffect={onRipple}
        inputStyle={inputStyle}
        onInputStyleChange={onInputStyleChange}
        layoutMode={layoutMode}
        onLayoutModeChange={onLayoutModeChange}
        layoutColorMode={layoutColorMode}
        onColorModeChange={onColorModeChange}
      />

      <CSSTransition
        classNames="layout-mask"
        timeout={{ enter: 200, exit: 200 }}
        in={mobileMenuActive}
        unmountOnExit
      >
        <div className="layout-mask p-component-overlay"></div>
      </CSSTransition>
    </>
  );
};

export default DashboardLayout;
